import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category?type=movie' },
  { label: '电视剧', to: '/category?type=tv' },
  { label: '资讯', to: '/news' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    const path = to.split('?')[0];
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled ? 'bg-background-50/95 border-background-200 backdrop-blur' : 'bg-background-50 border-transparent'
      }`}
    >
      <div className="flex h-16 md:h-[72px] items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-3 shrink-0 cursor-pointer">
          <span className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center text-background-50 font-heading text-xl font-bold">
            翔
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading text-lg font-bold text-foreground-950 tracking-wide">翔田千里</span>
            <span className="text-[11px] text-foreground-500 tracking-[0.3em] font-label">影视大全</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                isActive(item.to)
                  ? 'text-primary-700 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-md ml-auto">
          <div className="relative w-full">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索影片、电视剧或资讯"
              className="w-full h-10 pl-10 pr-24 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
            <button
              type="submit"
              className="absolute right-1 top-1 h-8 px-4 rounded-full bg-primary-600 text-background-50 text-xs font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="flex md:hidden items-center gap-2 ml-auto">
          <Link
            to="/search"
            className="w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-xl"></i>
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="w-10 h-10 flex items-center justify-center rounded-full text-foreground-800 hover:bg-background-100 cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-in">
          <form onSubmit={handleSearch} className="relative w-full mb-4">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索影片、电视剧或资讯"
              className="w-full h-11 pl-10 pr-4 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`py-3 px-3 rounded-md text-base font-medium cursor-pointer ${
                  isActive(item.to) ? 'text-primary-700 bg-primary-50' : 'text-foreground-800 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}